import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple, MapPin, InstagramLogo, YoutubeLogo, FacebookLogo, Camera } from "@phosphor-icons/react";
import { NAP, IMAGE_CREDITS } from "@/data/site";
import { useI18n } from "@/context/I18nContext";
import { track } from "@/lib/api";

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-border bg-secondary/40" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <Link to="/" className="flex items-center gap-2.5 mb-6">
            <img
              src="/mre-logo.webp"
              alt="MRE Developments logo"
              className="mre-logo h-11 w-11 object-contain"
            />
            <span className="font-display tracking-tight text-xl uppercase">MRE Developments</span>
          </Link>
          <p className="text-muted-foreground max-w-sm leading-relaxed">
            {t("footer.tagline")}
          </p>
          <p className="mt-6 font-mono-tech text-xs tracking-widest uppercase text-muted-foreground">
            {t("footer.roots")}
          </p>
        </div>

        <div className="md:col-span-3">
          <h3 className="font-mono-tech text-xs tracking-widest uppercase text-primary mb-5">{t("footer.explore")}</h3>
          <ul className="space-y-3">
            {[
              { to: "/services", key: "nav.services" },
              { to: "/digital", key: "nav.digital" },
              { to: "/locations", label: "Locations" },
              { to: "/about", key: "nav.about" },
              { to: "/contact", key: "nav.contact" },
              { to: "/chat", label: "Ask MRE" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-muted-foreground hover:text-primary transition-colors">
                  {l.label || t(l.key)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h3 className="font-mono-tech text-xs tracking-widest uppercase text-primary mb-5">{t("footer.getInTouch")}</h3>
          <ul className="space-y-4">
            <li>
              <a
                href={`tel:${NAP.phoneHref}`}
                onClick={() => track("click_to_call", { location: "footer" })}
                className="flex items-center gap-3 text-foreground hover:text-primary transition-colors"
                data-testid="footer-call"
              >
                <Phone size={18} className="text-primary" /> {NAP.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={`mailto:${NAP.email}`} className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                <EnvelopeSimple size={18} className="text-primary" /> {NAP.email}
              </a>
            </li>
            <li className="flex items-start gap-3 text-muted-foreground">
              <MapPin size={18} className="text-primary mt-0.5" />
              <span>
                <span className="block font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground/70 mb-1">
                  Head office
                </span>
                {NAP.street}, {NAP.city}, {NAP.region} {NAP.postalCode}
              </span>
            </li>
          </ul>

          {/* Service area — the Calgary address above is the head office, not
              the limit of who we serve. Spelled out so out-of-region visitors
              don't assume we only work in Calgary. */}
          <div className="mt-7 pt-6 border-t border-border/60" data-testid="footer-service-area">
            <h4 className="font-mono-tech text-xs tracking-widest uppercase text-primary mb-3">
              Where we work
            </h4>
            <p className="text-muted-foreground text-sm leading-relaxed">
              <span className="text-foreground">Construction</span> —{" "}
              {NAP.serviceArea.constructionCities.join(", ")} and the surrounding region.
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed mt-2">
              <span className="text-foreground">Branding, web, marketing &amp; AI</span> —{" "}
              delivered {NAP.serviceArea.digitalLong}.
            </p>
            <p className="text-muted-foreground/80 text-sm leading-relaxed mt-3">
              Outside these areas?{" "}
              <Link to="/contact" className="text-primary hover:underline">
                Get in touch
              </Link>{" "}
              — we travel for the right project.
            </p>
          </div>
          <div className="flex gap-3 mt-6">
            {[
              { href: NAP.social.instagram, Icon: InstagramLogo, label: "Instagram" },
              { href: NAP.social.facebook, Icon: FacebookLogo, label: "Facebook" },
              { href: NAP.social.youtube, Icon: YoutubeLogo, label: "YouTube" },
            ].map(({ href, Icon, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="grid place-items-center w-10 h-10 border border-border hover:border-primary hover:text-primary transition-colors"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* PHOTOGRAPHY CREDITS */}
      <div className="border-t border-border" data-testid="footer-credits">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
          <h3 className="flex items-center gap-2 font-mono-tech text-xs tracking-widest uppercase text-primary mb-2">
            <Camera size={15} weight="duotone" /> {t("footer.credits")}
          </h3>
          <p className="text-muted-foreground text-sm max-w-2xl leading-relaxed">
            {t("footer.creditsNote")}
          </p>
          <ul className="mt-4 flex flex-wrap gap-x-2 gap-y-2">
            {IMAGE_CREDITS.map((c, i) => (
              <li key={c.url} className="flex items-center">
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/80 hover:text-primary transition-colors text-sm"
                  data-testid={`image-credit-${i}`}
                >
                  {c.name}
                </a>
                {i < IMAGE_CREDITS.length - 1 && (
                  <span className="text-border ml-2 select-none">·</span>
                )}
              </li>
            ))}
            <li className="flex items-center">
              <span className="text-border ml-2 mr-2 select-none">·</span>
              <a
                href="https://unsplash.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono-tech text-xs tracking-widest uppercase text-muted-foreground hover:text-primary transition-colors"
                data-testid="footer-unsplash-link"
              >
                Unsplash
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground">
            © {new Date().getFullYear()} M.R.E. Developments Ltd. — {NAP.serviceArea.summary}
          </p>
          <Link
            to="/admin/login"
            className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground hover:text-primary transition-colors"
            data-testid="footer-admin-link"
          >
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
